import * as React from 'react'
import { useState, useCallback, useRef } from 'react'
import { connect } from 'react-redux'
import { RouteComponentProps, withRouter } from 'react-router'
import { login, LoginDispatch, register, RegisterDispatch, organizationListPath, Paths } from '@logic'
import { StateTransform } from '@src/index'
import { AuthModal } from '../components/auth-modal'
import { MarketingHeader, MarketingFooter } from '@modules/marketing'
import {
  LandingContainer,
  BgGradient1,
  BgGradient2,
  Main,
  HeroText,
  Highlight,
  Subtitle,
  InputContainer,
  InputWrapper,
  InputField,
  SubmitButton,
  ExamplePrompts,
  ExamplePrompt,
  BottomText,
  Features,
  Feature,
  FeatureIcon,
  AttachButton,
  FilePreviewContainer,
  FilePreviewChip,
  FileRemoveButton,
  DragOverlay,
} from '../styles/landing.styles'

// File type icons
const getFileIcon = (type: string) => {
  if (type.startsWith('image/')) {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
        <circle cx="8.5" cy="8.5" r="1.5"/>
        <polyline points="21 15 16 10 5 21"/>
      </svg>
    )
  }
  if (type.includes('spreadsheet') || type.includes('excel') || type.includes('csv')) {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
        <polyline points="14 2 14 8 20 8"/>
        <line x1="8" y1="13" x2="16" y2="13"/>
        <line x1="8" y1="17" x2="16" y2="17"/>
        <line x1="10" y1="9" x2="10" y2="21"/>
      </svg>
    )
  }
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
      <line x1="16" y1="13" x2="8" y2="13"/>
      <line x1="16" y1="17" x2="8" y2="17"/>
      <polyline points="10 9 9 9 8 9"/>
    </svg>
  )
}

// Allowed file types and max size
const ALLOWED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.csv', '.txt', '.md']
const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB
const MAX_FILES = 5

// Example prompts for quick start
const examples: Record<string, string> = {
  startup: "I'm starting a tech startup with 3 co-founders. We need to track our equity split (40/30/30), set up a vesting schedule, and prepare for our seed round.",
  fund: "I'm launching a venture fund with 5 limited partners. We need to track capital calls, distributions, and portfolio company investments.",
  spv: "I'm setting up an SPV to pool investment from 10 investors for a single deal. Need to track contributions and future returns.",
  esop: "I need to manage our company's employee stock option plan. We have 50 employees and want to track grants, vesting, and exercises."
}

interface StateProps {
  user?: any
}

interface DispatchProps {
  login: LoginDispatch
  register: RegisterDispatch
}

type Props = RouteComponentProps<any> & StateProps & DispatchProps

const LandingPageComponent: React.FC<Props> = (props) => {
  const { history, user } = props
  const [inputValue, setInputValue] = useState('')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [initialContext, setInitialContext] = useState('')
  const [isSignInMode, setIsSignInMode] = useState(false)
  const [attachedFiles, setAttachedFiles] = useState<File[]>([])
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Redirect if user is already logged in
  React.useEffect(() => {
    if (user) {
      history.push(organizationListPath)
    }
  }, [user, history])

  const validateFile = useCallback((file: File): string | null => {
    const ext = '.' + file.name.split('.').pop()?.toLowerCase()
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      return `File type ${ext} not supported`
    }
    if (file.size > MAX_FILE_SIZE) {
      return `File ${file.name} exceeds 10MB limit`
    }
    return null
  }, [])

  const handleFileSelect = useCallback((files: FileList | null) => {
    if (!files) return
    const validFiles: File[] = []
    for (let i = 0; i < files.length && validFiles.length + attachedFiles.length < MAX_FILES; i++) {
      const error = validateFile(files[i])
      if (!error) {
        validFiles.push(files[i])
      } else {
        console.warn(error)
      }
    }
    if (validFiles.length > 0) {
      setAttachedFiles(prev => [...prev, ...validFiles].slice(0, MAX_FILES))
    }
  }, [attachedFiles.length, validateFile])

  const handleRemoveFile = useCallback((index: number) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index))
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
    handleFileSelect(e.dataTransfer.files)
  }, [handleFileSelect])

  const handleGetStarted = useCallback(() => {
    if (!inputValue.trim() && attachedFiles.length === 0) {
      return
    }
    setInitialContext(inputValue.trim())
    setIsSignInMode(false)
    setIsModalOpen(true)
  }, [inputValue, attachedFiles.length])

  const handleSignInClick = useCallback(() => {
    setInitialContext('')
    setIsSignInMode(true)
    setIsModalOpen(true)
  }, [])

  const handleKeyPress = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleGetStarted()
    }
  }, [handleGetStarted])

  const setExample = useCallback((type: string) => {
    setInputValue(examples[type] || '')
  }, [])

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false)
  }, [])

  const handleAuthSuccess = useCallback((userData: any) => {
    // Store context for Equanaut if provided
    if (initialContext) {
      localStorage.setItem('equanaut_initial_context', JSON.stringify({
        context: initialContext,
        timestamp: Date.now(),
        isFirstLogin: true,
      }))
    }

    // Navigate based on email verification status
    if (userData?.user?.emailVerified === false) {
      history.push(Paths.verify)
    } else {
      history.push(organizationListPath)
    }
  }, [initialContext, history])

  return (
    <LandingContainer>
      <BgGradient1 />
      <BgGradient2 />

      <MarketingHeader />

      <Main>
        <HeroText>
          <h1>Tell Equanaut what you want to <Highlight>organize</Highlight></h1>
          <p>Describe your company, fund, or project and our AI assistant will set everything up for you</p>
          <Subtitle>Cap tables, stakeholders, documents - all configured automatically</Subtitle>
        </HeroText>

        <InputContainer>
          <InputWrapper
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            style={attachedFiles.length > 0 ? { borderRadius: '8px 8px 0 0' } : undefined}
          >
            <DragOverlay isDragging={isDragging}>Drop files here</DragOverlay>
            <AttachButton
              type="button"
              onClick={() => fileInputRef.current?.click()}
              title="Attach files (cap tables, org charts, contracts, logos)"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
              </svg>
            </AttachButton>
            <InputField
              type="text"
              placeholder="e.g., I'm starting a tech startup with 3 co-founders and we need to track our equity..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
            />
            <SubmitButton onClick={handleGetStarted}>Get Started</SubmitButton>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.md"
              style={{ display: 'none' }}
              onChange={(e) => {
                handleFileSelect(e.target.files)
                e.target.value = '' // Reset to allow re-selecting same file
              }}
            />
          </InputWrapper>
          {attachedFiles.length > 0 && (
            <FilePreviewContainer>
              {attachedFiles.map((file, i) => (
                <FilePreviewChip key={`${file.name}-${i}`}>
                  {getFileIcon(file.type)}
                  <span title={file.name}>{file.name}</span>
                  <FileRemoveButton
                    type="button"
                    onClick={() => handleRemoveFile(i)}
                    title="Remove file"
                  >
                    ×
                  </FileRemoveButton>
                </FilePreviewChip>
              ))}
            </FilePreviewContainer>
          )}
        </InputContainer>

        <ExamplePrompts>
          <ExamplePrompt onClick={() => setExample('startup')}>Tech startup with co-founders</ExamplePrompt>
          <ExamplePrompt onClick={() => setExample('fund')}>Investment fund with LPs</ExamplePrompt>
          <ExamplePrompt onClick={() => setExample('spv')}>SPV for a single deal</ExamplePrompt>
          <ExamplePrompt onClick={() => setExample('esop')}>ESOP management for my company</ExamplePrompt>
        </ExamplePrompts>

        <BottomText>
          Already have an account? <a onClick={handleSignInClick}>Sign in</a> to continue where you left off
        </BottomText>

        <Features id="features">
          <Feature>
            <FeatureIcon>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
                <line x1="12" y1="22.08" x2="12" y2="12"/>
              </svg>
            </FeatureIcon>
            <h3>AI-Powered Setup</h3>
            <p>Equanaut configures your entire workspace based on your description</p>
          </Feature>
          <Feature>
            <FeatureIcon>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <polyline points="12 6 12 12 16 14"/>
              </svg>
            </FeatureIcon>
            <h3>Ready in Seconds</h3>
            <p>No manual configuration - just describe and go</p>
          </Feature>
          <Feature>
            <FeatureIcon>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
            </FeatureIcon>
            <h3>Guided by AI</h3>
            <p>Equanaut stays with you to help manage everything</p>
          </Feature>
        </Features>
      </Main>

      <MarketingFooter />

      <AuthModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        initialContext={initialContext}
        isSignInMode={isSignInMode}
        onAuthSuccess={handleAuthSuccess}
        login={props.login}
        register={props.register}
        attachedFiles={attachedFiles}
      />
    </LandingContainer>
  )
}

const mapStateToProps: StateTransform<StateProps> = (state) => ({
  user: state.user,
})

const mapDispatchToProps = {
  login,
  register,
}

export const LandingPage = connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(LandingPageComponent))
